/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, Text as TextCore, type TextProps as TextCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';

interface TextProps extends Omit<TextCoreProps, 'style' | 'className' | 'elementType'> {
  /** The size variant of the text. (Default: 16) */
  size?: keyof typeof sizeVariants;
  /** The color variant of the text. (Default: primary) */
  color?: keyof typeof colorVariants;
  /** The weight variant of the text. (Default: regular) */
  weight?: keyof typeof weightVariants;
  style?: stylex.StyleXStyles;
}

const TextContext = createContext<ContextValue<TextProps, HTMLElement>>({});

const Text = forwardRef((props: TextProps, ref: ForwardedRef<HTMLElement>) => {
  [props, ref] = useContextProps(props, ref, TextContext);

  const { size = 16, color = 'primary', weight = 'regular', style, ...coreProps } = props;

  return (
    <TextCore
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        colorVariants[color],
        weightVariants[weight],
        letterSpacingVariants[`${weight}${size.toString()}` as keyof typeof letterSpacingVariants],
        style,
      )}
      {...coreProps}
    />
  );
});

const styles = stylex.create({
  base: {
    fontFamily: 'BasierCircle',
    fontFeatureSettings: 'liga off, clig off',
  },
});

const sizeVariants = stylex.create({
  20: {
    fontSize: 20,
    lineHeight: '24px'
  },
  18: {
    fontSize: 18,
    lineHeight: '22px'
  },
  16: {
    fontSize: 16,
    lineHeight: '20px'
  },
  14: {
    fontSize: 14,
    lineHeight: '18px'
  },
  13: {
    fontSize: 13,
    lineHeight: '16px'
  },
  12: {
    fontSize: 12,
    lineHeight: '16px'
  },
  11: {
    fontSize: 11,
    lineHeight: '12px'
  },
  10: {
    fontSize: 10,
    lineHeight: '12px'
  },
  8: {
    fontSize: 8,
    lineHeight: '10px'
  },
});

const colorVariants = stylex.create({
  primary: { color: $.textPrimary },
  secondary: { color: $.textSecondary },
  tertiary: { color: $.textTertiary },
  quaternary: { color: $.textQuaternary },
  placeholder: { color: $.textPlaceholder },
  disabled: { color: $.textDisabled },
  inverted: { color: $.textInverted },
  interactive: { color: $.textInteractive },
  interactiveHover: { color: $.textInteractiveHover },
  positive: { color: $.textPositive },
  positiveHover: { color: $.textPositiveHover },
  critical: { color: $.textCritical },
  criticalHover: { color: $.textCriticalHover },
  white: { color: $.textWhite },
  black: { color: $.textBlack },
  // ! The following colors have been added because the icon uses them (ask design team to standardize colors)
  red: { color: $.colorRed50 },
  yellow: { color: $.colorSun80 },
  orange: { color: $.colorPumpkin80 },
  turquoise: { color: $.colorTurquoise80 },
  blueLight: { color: $.colorOcean80 },
  blueDark: { color: $.colorSky80 },
  violet: { color: $.colorPlum60 },
  // ! The following colors have been added because the number badge uses them (ask design team to standardize colors)
  buttonContentDisabled: { color: $.buttonContentDisabled },
  // ! The following colors have been added because the button uses them (ask design team to standardize colors)
  primaryHover: { color: $.buttonContentPrimaryHover },
  tertiaryHover: { color: $.buttonContentTertiaryHover },
  // ! The following colors have been added because the snackbar uses them (ask design team to standardize colors)
  cucumber80: { color: $.accentCucumber80 },
  tertiaryDark: { color: '#C8CFFF' },
  // ! The following colors have been added because the social login button uses them (ask design team to standardize colors)
  buttonContentTertiary: { color: $.buttonContentTertiary },
});

const weightVariants = stylex.create({
  semibold: { fontWeight: 600 },
  medium: { fontWeight: 500 },
  regular: { fontWeight: 400 },
});

const letterSpacingVariants = stylex.create({
  semibold20: { letterSpacing: -0.04 },
  medium20: { letterSpacing: -0.24 },
  regular20: { letterSpacing: -0.4 },
  semibold18: { letterSpacing: -0.09 },
  medium18: { letterSpacing: -0.216 },
  regular18: { letterSpacing: -0.396 },
  semibold16: { letterSpacing: 0 },
  medium16: { letterSpacing: -0.1 },
  regular16: { letterSpacing: -0.32 },
  semibold14: { letterSpacing: 0.14 },
  medium14: { letterSpacing: -0.112 },
  regular14: { letterSpacing: -0.252 },
  semibold13: { letterSpacing: 0.1 },
  medium13: { letterSpacing: -0.065 },
  regular13: { letterSpacing: -0.26 },
  semibold12: { letterSpacing: 0.12 },
  medium12: { letterSpacing: 0.12 },
  regular12: { letterSpacing: -0.12 },
  semibold11: { letterSpacing: -0.055 },
  medium11: { letterSpacing: -0.055 },
  regular11: { letterSpacing: -0.055 },
  semibold10: { letterSpacing: -0.05 },
  medium10: { letterSpacing: -0.05 },
  regular10: { letterSpacing: -0.05 },
  semibold8: { letterSpacing: 0 },
  medium8: { letterSpacing: 0 },
  regular8: { letterSpacing: 0 },
});

export { Text, TextContext, type TextProps };
